import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[8]

export const KWP_8_INSTALACJA_FOTOWOLTAICZNA: ProgrammaticLandingPageConfig = {
  slug: 'instalacja-fotowoltaiczna-8-kwp-cena',
  lang: 'pl',
  topicId: 'kwp-kosten-8',
  title: 'Instalacja fotowoltaiczna 8 kWp: cena i wartości orientacyjne',
  metaDescription:
    'Ile kosztuje instalacja fotowoltaiczna 8 kWp? Powierzchnia dachu, liczba paneli, ' +
    'rozbicie kosztów i bezpłatne porównanie cen dla gospodarstw 3-4-osobowych.',
  keywords: [
    'instalacja fotowoltaiczna 8 kWp cena',
    'panele słoneczne 8 kWp koszt',
    'ile kosztuje instalacja 8 kWp',
    '8 kWp instalacja fotowoltaiczna dom',
  ],
  eyebrow: 'Wielkość instalacji: 8 kWp',
  h1: 'Instalacja fotowoltaiczna 8 kWp: cena i wartości orientacyjne',
  intro:
    'Instalacja 8 kWp to popularna wielkość pośrednia dla gospodarstw 3-4-osobowych z nieco ' +
    'wyższym zużyciem lub planowanym samochodem elektrycznym. Znajdziesz tu wartości orientacyjne ' +
    'dotyczące powierzchni dachu i liczby paneli, rozbicie kosztów, a tuż poniżej kalkulator z już ' +
    'wypełnioną średnią ceną dla 8 kWp, obliczoną na podstawie prawdziwych ofert firm ' +
    'rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp', kwp: 8 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiednia jest instalacja 8 kWp?' },
    {
      type: 'paragraph',
      text:
        'Wartość orientacyjna: 8 kWp zwykle pasuje do gospodarstwa 3-4-osobowego z rocznym ' +
        'zużyciem ok. 4 500-6 000 kWh, na przykład gdy planowany jest samochód elektryczny, ale nie ' +
        'trzeba jeszcze uwzględniać pompy ciepła. Jeśli dojdzie pompa ciepła, zwykle lepiej pasuje ' +
        'większa instalacja (od 12 kWp wzwyż).',
    },
    { type: 'heading', text: 'Dach i panele: czego potrzebuje instalacja 8 kWp' },
    {
      type: 'paragraph',
      text:
        `Wartość orientacyjna: ok. ${FACTS.roofAreaM2} m² powierzchni dachu i ok. ` +
        `${FACTS.moduleCount} paneli przy typowych modułach ~440 Wp. Roczna produkcja przy ` +
        `orientacji południowej bez zacienienia wynosi ok. ${FACTS.annualYieldKwh.toLocaleString('pl-PL')} kWh. ` +
        'Dokładne obliczenie dla Twojego dachu daje bezpłatny kalkulator doboru instalacji.',
    },
    { type: 'heading', text: 'Składniki kosztów instalacji fotowoltaicznej' },
    {
      type: 'list',
      items: [
        'Panele: największa pojedyncza pozycja kosztowa, zwykle około jednej trzeciej całości',
        'Falownik: przy 8 kWp często wystarcza jeszcze pojedynczy falownik stringowy',
        'Montaż/robocizna: mocowanie na dachu, okablowanie',
        'Rusztowanie: zależnie od wysokości i dostępności dachu',
        'Instalacja elektryczna: dostosowanie rozdzielnicy, podłączenie do sieci domowej',
        'Zgłoszenie: rejestracja u operatora sieci i w odpowiednich rejestrach',
      ],
    },
    {
      type: 'paragraph',
      text:
        'W porównaniu do 5 kWp, przy 8 kWp zgłoszenie i podstawowe rusztowanie rozkładają się już na ' +
        'większą zainstalowaną moc - cena za kWp wypada zwykle nieco korzystniej.',
    },
    { type: 'heading', text: 'Amortyzacja i autokonsumpcja' },
    {
      type: 'paragraph',
      text:
        'Bez magazynu energii udział autokonsumpcji w instalacji 8 kWp wynosi zwykle ok. 25-30 %, a ' +
        'z odpowiednio dobranym magazynem energii i samochodem elektrycznym jako dodatkowym ' +
        'odbiornikiem w ciągu dnia - zwykle wyraźnie więcej. Dokładne obliczenie dla Twojego dachu ' +
        'daje bezpłatny kalkulator doboru instalacji.',
    },
  ],
  faq: [
    {
      q: 'Dla jakich gospodarstw odpowiednia jest instalacja 8 kWp?',
      a: 'Zwykle dla gospodarstw 3-4-osobowych z nieco wyższym zużyciem lub planowanym samochodem elektrycznym, ale bez pompy ciepła. Z pompą ciepła lepiej pasuje większa wielkość.',
    },
    {
      q: 'Czy przy 8 kWp potrzebuję falownika trójfazowego?',
      a: 'Niekoniecznie - wiele instalacji 8 kWp działa jeszcze na pojedynczym falowniku stringowym. Dokładna potrzeba zależy od przyłącza elektrycznego domu i wybranej technologii - firma rzemieślnicza wyjaśnia to w ofercie.',
    },
    {
      q: 'Ile powierzchni dachu potrzebuję do 8 kWp?',
      a: `Wartość orientacyjna: ok. ${FACTS.roofAreaM2} m², w zależności od wybranego typu paneli i orientacji dachu.`,
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Instalacja fotowoltaiczna 5 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-5-kwp-cena' },
    { label: 'Instalacja fotowoltaiczna 10 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-10-kwp-cena' },
    { label: 'Instalacja fotowoltaiczna 12 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-12-kwp-cena' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
  ],
}
